import React, { useEffect, useState } from 'react';
import { Card, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Loader2, CheckCircle2, XCircle, Sparkles, ArrowLeft, RefreshCw } from 'lucide-react';
import { toast } from 'sonner';
import {
  fetchTriviaDaily, submitTrivia, fetchTriviaBonus,
} from '../../services/gamesApi';
import GameResultCard from './GameResultCard';
import { useLanguage } from '../../contexts/LanguageContext';

/**
 * Daily Bible Trivia — 5 multiple-choice questions/day with optional
 * AI-generated bonus question after submission.
 */
const BibleTriviaGame = ({ language = 'en', onBack, onScored }) => {
  const { t } = useLanguage();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [picks, setPicks] = useState({});  // { q_id: chosen_index }
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null); // submit response
  const [bonus, setBonus] = useState(null);
  const [bonusPick, setBonusPick] = useState(null);
  const [bonusLoading, setBonusLoading] = useState(false);

  useEffect(() => {
    setLoading(true);
    fetchTriviaDaily(language)
      .then(setData)
      .catch(() => toast.error(t('games.trivia.failedLoad')))
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [language]);

  const allAnswered = data && data.questions.every((q) => picks[q.id] !== undefined);

  const onSubmit = async () => {
    if (!allAnswered) return;
    setSubmitting(true);
    try {
      const answers = data.questions.map((q) => ({ id: q.id, picked: picks[q.id] }));
      const r = await submitTrivia(answers);
      setResult(r);
      onScored?.(r);
    } catch {
      toast.error(t('games.submitFailed'));
    } finally {
      setSubmitting(false);
    }
  };

  const onBonus = async () => {
    setBonusLoading(true);
    try {
      const b = await fetchTriviaBonus();
      setBonus(b);
      setBonusPick(null);
    } catch {
      toast.error(t('games.trivia.bonusUnavailable'));
    } finally {
      setBonusLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12 text-muted-foreground">
        <Loader2 className="w-5 h-5 animate-spin mr-2" />
        {t('games.loading')}
      </div>
    );
  }
  if (!data) return null;

  // Result screen.
  if (result) {
    const detailsById = Object.fromEntries(result.details.map((d) => [d.id, d]));
    return (
      <div className="space-y-4" data-testid="trivia-result-screen">
        <Button onClick={onBack} variant="ghost" size="sm" className="-ml-2">
          <ArrowLeft className="w-4 h-4 mr-1" />
          {t('games.back')}
        </Button>

        <GameResultCard
          language={language}
          gameTitle={t('games.trivia.title')}
          correct={result.correct}
          total={result.total}
          score={result.score}
          perfect={result.perfect}
          streak={result.streak}
          newTotal={result.new_total}
        />

        <div className="space-y-2">
          {data.questions.map((q, idx) => {
            const d = detailsById[q.id];
            const correctIdx = d?.answer_index;
            const pickedIdx = d?.picked;
            return (
              <Card key={q.id} className="border" data-testid={`trivia-review-${q.id}`}>
                <CardContent className="p-3 text-sm">
                  <div className="flex items-start gap-2">
                    {d?.correct
                      ? <CheckCircle2 className="w-4 h-4 text-green-600 mt-0.5 flex-shrink-0" />
                      : <XCircle className="w-4 h-4 text-red-500 mt-0.5 flex-shrink-0" />}
                    <div>
                      <p className="font-medium">{idx + 1}. {q.q}</p>
                      <p className="text-xs text-muted-foreground mt-1">
                        {t('games.trivia.correctLabel')}{' '}
                        <span className="font-semibold">{q.options[correctIdx]}</span>
                        {!d?.correct && pickedIdx !== undefined && (
                          <> · {t('games.trivia.yourPick')} {q.options[pickedIdx]}</>
                        )}
                        {d?.reference && <> · <span className="italic">{d.reference}</span></>}
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>

        {/* AI bonus round */}
        <div className="pt-4 border-t border-border/60 space-y-3">
          {!bonus && (
            <Button
              onClick={onBonus}
              disabled={bonusLoading}
              className="w-full bg-gradient-to-r from-purple-600 to-pink-600 text-white hover:from-purple-700 hover:to-pink-700"
              data-testid="trivia-bonus-btn"
            >
              {bonusLoading
                ? <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                : <Sparkles className="w-4 h-4 mr-2" />}
              {t('games.trivia.aiBonusBtn')}
            </Button>
          )}
          {bonus && (
            <Card data-testid="trivia-bonus-card">
              <CardContent className="p-4 space-y-3 text-sm">
                <Badge variant="secondary" className="text-[10px]">
                  <Sparkles className="w-3 h-3 mr-1" />
                  {t('games.trivia.aiBonus')}
                </Badge>
                <p className="font-medium">{bonus.q}</p>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                  {bonus.options.map((o, i) => {
                    const isPicked = bonusPick === i;
                    const reveal = bonusPick !== null;
                    const isCorrect = i === bonus.answer;
                    return (
                      <button
                        key={i}
                        type="button"
                        disabled={reveal}
                        onClick={() => setBonusPick(i)}
                        data-testid={`trivia-bonus-opt-${i}`}
                        className={`text-left px-3 py-2 rounded-md border text-xs transition-colors ${
                          reveal
                            ? isCorrect
                              ? 'border-green-500 bg-green-500/10'
                              : isPicked
                                ? 'border-red-500 bg-red-500/10'
                                : 'border-border opacity-70'
                            : isPicked
                              ? 'border-purple-500 bg-purple-500/10'
                              : 'border-border hover:border-primary/50'
                        }`}
                      >
                        {o}
                      </button>
                    );
                  })}
                </div>
                {bonusPick !== null && (
                  <p className="text-xs text-muted-foreground">
                    {bonusPick === bonus.answer
                      ? t('games.trivia.correct')
                      : t('games.trivia.incorrect')}
                    {bonus.reference && <> · <span className="italic">{bonus.reference}</span></>}
                  </p>
                )}
                <Button onClick={onBonus} disabled={bonusLoading} variant="outline" size="sm">
                  <RefreshCw className="w-3.5 h-3.5 mr-1.5" />
                  {t('games.trivia.anotherBonus')}
                </Button>
              </CardContent>
            </Card>
          )}
        </div>
      </div>
    );
  }

  // Question screen.
  return (
    <div className="space-y-4" data-testid="trivia-game-screen">
      <Button onClick={onBack} variant="ghost" size="sm" className="-ml-2">
        <ArrowLeft className="w-4 h-4 mr-1" />
        {t('games.back')}
      </Button>

      <div className="flex items-center justify-between">
        <h2 className="text-lg font-bold">
          {t('games.trivia.title')}
        </h2>
        <Badge variant="outline" className="text-[10px]">
          {data.date} · {t('games.trivia.questions5')}
        </Badge>
      </div>

      {data.questions.map((q, idx) => (
        <Card key={q.id} className="border" data-testid={`trivia-q-${q.id}`}>
          <CardContent className="p-4 space-y-3 text-sm">
            <p className="font-medium">{idx + 1}. {q.q}</p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              {q.options.map((o, i) => {
                const isPicked = picks[q.id] === i;
                return (
                  <button
                    key={i}
                    type="button"
                    onClick={() => setPicks((p) => ({ ...p, [q.id]: i }))}
                    data-testid={`trivia-opt-${q.id}-${i}`}
                    className={`text-left px-3 py-2 rounded-md border text-xs transition-colors ${
                      isPicked
                        ? 'border-purple-500 bg-purple-500/10'
                        : 'border-border hover:border-primary/50'
                    }`}
                  >
                    {o}
                  </button>
                );
              })}
            </div>
          </CardContent>
        </Card>
      ))}

      <Button
        onClick={onSubmit}
        disabled={!allAnswered || submitting}
        className="w-full bg-gradient-to-r from-purple-600 to-pink-600 text-white hover:from-purple-700 hover:to-pink-700"
        data-testid="trivia-submit-btn"
      >
        {submitting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
        {t('games.submitAnswers')}
      </Button>
    </div>
  );
};

export default BibleTriviaGame;
